import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { planAllows, PLAN_META } from "@/lib/format";
import {
  LayoutDashboard, Users, TrendingUp, Package, FileText, Receipt, Briefcase,
  CalendarDays, PiggyBank, BarChart3, UserCog, Clock, Boxes as BoxesIcon, Bot,
  Store, Globe, Settings as SettingsIcon, ShieldCheck, LogOut, Menu, Plus, Lock, ChevronDown, Eye, Gift, X,
} from "lucide-react";
import { Btn } from "./ui";
import QuickCreate from "./QuickCreate";
import { clearImpersonation } from "@/lib/api";

const GROUPS = [
  { title: "Overview", items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  ]},
  { title: "Sales", items: [
    { to: "/app/customers", label: "Customer Hub", icon: Users, feature: "customers" },
    { to: "/app/leads", label: "Leads", icon: TrendingUp, feature: "leads" },
    { to: "/app/products", label: "Products & Services", icon: Package, feature: "products" },
    { to: "/app/quotes", label: "Quotes", icon: FileText, feature: "quotes" },
    { to: "/app/invoices", label: "Invoices", icon: Receipt, feature: "invoices" },
  ]},
  { title: "Operations", items: [
    { to: "/app/jobs", label: "Jobs", icon: Briefcase, feature: "jobs" },
    { to: "/app/planner", label: "Planner", icon: CalendarDays, feature: "planner" },
    { to: "/app/expenses", label: "Expenses", icon: PiggyBank, feature: "expenses" },
    { to: "/app/reports", label: "Reports", icon: BarChart3, feature: "reports" },
  ]},
  { title: "Team & Stock", items: [
    { to: "/app/employees", label: "Employees", icon: UserCog, feature: "employees" },
    { to: "/app/timekeeping", label: "Time Clock", icon: Clock, feature: "timekeeping" },
    { to: "/app/materials", label: "Materials", icon: BoxesIcon, feature: "materials" },
  ]},
  { title: "More", items: [
    { to: "/app/alfie", label: "Alfie AI", icon: Bot, feature: "alfie" },
    { to: "/app/services", label: "Amden Services", icon: Store, feature: "services" },
    { to: "/app/website", label: "Website Add-on", icon: Globe, feature: "website" },
    { to: "/app/settings", label: "Settings", icon: SettingsIcon },
  ]},
];

const MOBILE = [
  { to: "/app", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/app/jobs", label: "Jobs", icon: Briefcase },
  { quick: true },
  { to: "/app/invoices", label: "Invoices", icon: Receipt },
  { menu: true, label: "Menu", icon: Menu },
];

// Employees can only clock in/out — restrict them to the Time Clock.
const EMPLOYEE_ALLOWED = ["/app/timekeeping"];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [quick, setQuick] = useState(false);
  const plan = user?.business?.plan || "free";
  const isEmployee = user?.role === "employee";
  const impersonated = !!user?._impersonated;
  const trialEnd = user?.business?.trial_end;
  const trialDaysLeft = trialEnd ? Math.ceil((trialEnd * 1000 - Date.now()) / 86400000) : 0;
  const [trialDismissed, setTrialDismissed] = useState(() => sessionStorage.getItem("amden_trial_banner_dismissed") === "1");
  const showTrial = trialDaysLeft >= 1 && !isEmployee && !impersonated && !trialDismissed;
  const dismissTrial = () => { sessionStorage.setItem("amden_trial_banner_dismissed", "1"); setTrialDismissed(true); };
  const mobileItems = isEmployee
    ? [{ to: "/app/timekeeping", label: "Time Clock", icon: Clock }]
    : (impersonated ? MOBILE.filter((m) => !m.quick) : MOBILE);

  const doLogout = async () => { await logout(); nav("/login"); };

  const exitImpersonation = () => {
    clearImpersonation();
    window.close();
    // Fallback if the browser blocks closing a script-opened tab.
    window.location.replace("/admin");
  };

  // Employees are limited to the Time Clock only.
  if (isEmployee && loc.pathname !== "/app/timekeeping") {
    return <Navigate to="/app/timekeeping" replace />;
  }

  const NavItem = ({ item, onClick }) => {
    const allowed = !item.feature || planAllows(plan, item.feature);
    if (isEmployee && !EMPLOYEE_ALLOWED.includes(item.to)) return null;
    return (
      <NavLink to={item.to} end={item.end} onClick={onClick}
        data-testid={`nav-${item.to === "/app" ? "dashboard" : item.to.split("/").pop()}`}
        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-blue-50 text-blue-accent" : "text-slate-600 hover:bg-slate-100 hover:text-navy"}`}>
        <item.icon size={18} />
        <span className="flex-1">{item.label}</span>
        {!allowed && <Lock size={13} className="text-slate-300" />}
      </NavLink>
    );
  };

  const SidebarContent = ({ onNav }) => (
    <>
      <div className="px-4 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2 font-display font-extrabold text-lg text-navy">
          <span className="h-8 w-8 rounded-full overflow-hidden"><img src="/amden-logo.png?v=2" alt="Amden One" className="h-full w-full object-cover" /></span>
          Amden<span className="text-blue-accent">One</span>
        </div>
        <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy truncate" data-testid="workspace-name">{user?.business?.name || "Workspace"}</p>
            <p className="text-xs text-slate-400">{PLAN_META[plan].name} plan</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto amden-scroll px-3 py-4 space-y-5">
        {GROUPS.map((g) => {
          const items = g.items.filter((it) => !isEmployee || EMPLOYEE_ALLOWED.includes(it.to));
          if (!items.length) return null;
          return (
            <div key={g.title}>
              <p className="px-3 text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1">{g.title}</p>
              <div className="space-y-0.5">{items.map((it) => <NavItem key={it.to} item={it} onClick={onNav} />)}</div>
            </div>
          );
        })}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-navy text-white grid place-items-center text-sm font-semibold">{user?.name?.[0] || "U"}</div>
          <div className="min-w-0 flex-1"><p className="text-sm font-medium text-navy truncate">{user?.name}</p><p className="text-xs text-slate-400 capitalize">{user?.role}</p></div>
          <button onClick={doLogout} data-testid="logout-btn" className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-600 transition-colors"><LogOut size={18} /></button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-slate-200 fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col shadow-xl">
            <SidebarContent onNav={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        {impersonated && (
          <div data-testid="impersonation-banner" className="z-40 bg-amber-500 text-white text-sm font-medium px-4 py-2 flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 min-w-0"><Eye size={15} className="shrink-0" /> <span className="truncate">Support session — acting as support for <b>{user?.business?.name}</b>. Everything you do is recorded.</span></span>
            <button onClick={exitImpersonation} data-testid="exit-impersonation" className="shrink-0 px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 transition-colors">End session</button>
          </div>
        )}
        {showTrial && (
          <div data-testid="trial-banner" className="z-40 bg-blue-600 text-white text-sm font-medium px-4 py-2 flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 min-w-0"><Gift size={15} className="shrink-0" /> <span className="truncate"><b>{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left</b> in your free trial — you won't be charged until {new Date(trialEnd * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short" })}.</span></span>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => nav("/app/settings")} data-testid="trial-manage" className="px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 transition-colors">Manage plan</button>
              <button onClick={dismissTrial} data-testid="trial-dismiss" className="p-1 rounded-md hover:bg-white/20 transition-colors" aria-label="Dismiss trial banner"><X size={15} /></button>
            </div>
          </div>
        )}
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 h-14">
          <button onClick={() => setDrawer(true)} data-testid="mobile-drawer-toggle" className="p-2 -ml-2 text-navy"><Menu /></button>
          <span className="font-display font-bold text-navy">Amden<span className="text-blue-accent">One</span></span>
          <button onClick={doLogout} className="p-2 -mr-2 text-slate-400"><LogOut size={20} /></button>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 flex items-center justify-around h-16 px-2">
        {mobileItems.map((m, i) => {
          if (m.quick) return (
            <button key={i} onClick={() => setQuick(true)} data-testid="mobile-quick-create"
              className="grid place-items-center h-12 w-12 -mt-4 rounded-full bg-blue-600 text-white shadow-lg"><Plus size={24} /></button>
          );
          if (m.menu) return (
            <button key={i} onClick={() => setDrawer(true)} className="flex flex-col items-center gap-0.5 text-slate-500 text-xs">
              <m.icon size={20} />{m.label}
            </button>
          );
          return (
            <NavLink key={i} to={m.to} end={m.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-xs ${isActive ? "text-blue-accent" : "text-slate-500"}`}>
              <m.icon size={20} />{m.label}
            </NavLink>
          );
        })}
      </nav>

      <QuickCreate open={quick} onOpenChange={setQuick} />

      {/* Alfie float (desktop) */}
      {planAllows(plan, "alfie") && !isEmployee && !impersonated && loc.pathname !== "/app/alfie" && (
        <button onClick={() => nav("/app/alfie")} data-testid="alfie-ai-chat-trigger"
          className="hidden lg:flex fixed bottom-6 right-6 z-30 items-center gap-2 px-4 py-3 rounded-full bg-navy text-white shadow-xl hover:bg-slate-800 transition-colors">
          <Bot size={20} /> <span className="font-semibold text-sm">Ask Alfie</span>
        </button>
      )}
    </div>
  );
}
